/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { gnoment, Gnoment } from 'shared-ui/gnoment';
import filter from 'lodash/filter';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import {
    Select,
    Icon,
    FlexRow,
    FlexCol,
    Button,
    VisualOnlyLabel,
    Counter,
} from 'sarsaparilla';
import SingleDatePickerCalendar from '../../containers/single-date-picker-calendar';
import {
    dateSelectionDefault,
    dateSelectionProps,
} from '../../props/multi-division/DateSelection';
import { getCurrentTime, getSelectableSeasonalDivisions } from '../../utils/utilsNew';
import { LOTTERY_PREFERENCE_TYPES } from '../../constants';
import DateRangeGroupSizeItem from './PreferredDatesItems/DateRangeGroupSizeItem';

export default class PreferredDatesItem extends React.Component {
    static propTypes = {
        selection: dateSelectionProps.isRequired,
        onChange: PropTypes.func.isRequired,
        onRemove: PropTypes.func.isRequired,
        index: PropTypes.number.isRequired,
        divisions: PropTypes.shape({}),
        seasonStart: PropTypes.instanceOf(Gnoment),
        maxGroupSize: PropTypes.number,
        minGroupSize: PropTypes.number,
        blockFilter: PropTypes.func,
        lengthOfStayLimit: PropTypes.number,
        enableMinMaxGroupSelect: PropTypes.bool,
        enableLengthOfStaySelect: PropTypes.bool,
        validationErrors: PropTypes.shape({
            divisionRequired: PropTypes.string,
            choiceRequired: PropTypes.string,
            groupMaxRequired: PropTypes.string,
            lengthOfStayRequired: PropTypes.string,
            dateRangeRequired: PropTypes.string,
        }),
        lotteryPreferenceType: PropTypes.number,
        isCommercialUser: PropTypes.bool,
        enableMaxOnlyGroupSelect: PropTypes.bool,
    };

    static defaultProps = {
        maxGroupSize: 10,
        minGroupSize: 0,
    };

    static getDivisionOptions(divisions) {
        const filteredDivisions = filter(
            divisions,
            (div) => div.type !== 'Hidden' && !div.is_hidden
        );
        const value = Object.keys(filteredDivisions)
            .filter((key) => Object.hasOwn(filteredDivisions, key))
            .map((division) => ({
                label: filteredDivisions[division].name,
                value: filteredDivisions[division].id,
            }));
        value.unshift({ label: 'Select from list', value: '' });
        return value;
    }

    static getGroupSizeOptions(min, max) {
        const result = [];
        result.push({ label: '# people', value: '0' });

        for (let i = min; i <= max; i++) {
            if (i !== 0) {
                result.push({ label: `${i}`, value: `${i}` });
            }
        }
        return result;
    }

    handleChange = (valuePair) => {
        this.props.onChange({ ...this.props.selection, ...valuePair });
    };

    renderDivisionSelect(index) {
        return (
            <FlexCol md={6}>
                <div className="rec-form-item-wrap">
                    <VisualOnlyLabel isRequired>Entrance</VisualOnlyLabel>
                    <Select
                        label="Division"
                        isLabelVisible={false}
                        isRequired
                        onChange={(event) =>
                            this.handleChange({
                                division_id: event.target.value,
                                preferred_date: null,
                            })
                        }
                        options={PreferredDatesItem.getDivisionOptions(
                            this.props.divisions
                        )}
                        value={this.props.selection.division_id}
                        className={get(this.props, 'validationErrors.divisionRequired')}
                        id={`divisionSelect-${index}`}
                    />
                </div>
            </FlexCol>
        );
    }

    renderMaxOnlyGroupSelect(selectedGroupSize, maxGroupSize, index) {
        return (
            <FlexCol md={3}>
                <div className="rec-form-item-wrap">
                    <VisualOnlyLabel htmlFor={`groupSize-${index}`} isRequired>
                        Group Size
                    </VisualOnlyLabel>
                    <Counter
                        id={`groupSize-${index}`}
                        count={selectedGroupSize}
                        max={maxGroupSize}
                        validationClass={get(
                            this.props,
                            'validationErrors.groupMaxRequired',
                            ''
                        )}
                        onCountChange={(ct) =>
                            this.handleChange({ group_min_size: ct, group_max_size: ct })
                        }
                    />
                </div>
            </FlexCol>
        );
    }

    renderMinMaxGroupSelect(lowestGroupMax, highestGroupMin, idx) {
        return [
            <FlexCol md={4} key="min-group-size-select">
                <div className="rec-form-item-wrap" key={`min-group-select-${idx}`}>
                    <VisualOnlyLabel isRequired>Min Group Size</VisualOnlyLabel>
                    <Select
                        label="Minimum group size"
                        isRequired
                        isLabelVisible={false}
                        options={PreferredDatesItem.getGroupSizeOptions(
                            this.props.minGroupSize,
                            highestGroupMin
                        )}
                        onChange={(event) =>
                            this.handleChange({
                                group_min_size: parseInt(event.target.value, 10),
                            })
                        }
                        value={this.props.selection.group_min_size.toString()}
                        id={`minGroupSelect-${idx}`}
                    />
                </div>
            </FlexCol>,
            <FlexCol md={4} key="max-group-size-select">
                <div className="rec-form-item-wrap" key={`max-group-select-${idx}`}>
                    <VisualOnlyLabel isRequired>Max Group Size</VisualOnlyLabel>
                    <Select
                        label="Maximum group size"
                        isLabelVisible={false}
                        isRequired
                        options={PreferredDatesItem.getGroupSizeOptions(
                            lowestGroupMax,
                            this.props.maxGroupSize
                        )}
                        onChange={(event) =>
                            this.handleChange({
                                group_max_size: parseInt(event.target.value, 10),
                            })
                        }
                        value={this.props.selection.group_max_size.toString()}
                        className={get(this.props, 'validationErrors.groupMaxRequired')}
                        id={`maxGroupSelect-${idx}`}
                    />
                </div>
            </FlexCol>,
        ];
    }

    renderLengthOfStaySelect(lengthOfStays) {
        const lengthOfStay = get(this.props, 'selection.length_of_stay') || 0;
        return (
            <FlexCol md={6}>
                <div className="rec-form-item-wrap">
                    <VisualOnlyLabel isRequired>Length of Stay</VisualOnlyLabel>
                    <Select
                        label="Length of stay"
                        isLabelVisible={false}
                        isRequired
                        options={[
                            { value: '', label: 'Select # of days' },
                            ...lengthOfStays.map((option) => ({
                                value: option.toString(),
                                label: `${option} ${option > 1 ? 'days' : 'day'}`,
                            })),
                        ]}
                        id={`stayLengthSelect-${this.props.index}`}
                        className={get(
                            this.props,
                            'validationErrors.lengthOfStayRequired'
                        )}
                        value={lengthOfStay || ''}
                        onChange={(event) => {
                            this.handleChange({
                                length_of_stay: parseInt(event.target.value, 10),
                            });
                        }}
                    />
                </div>
            </FlexCol>
        );
    }

    renderRangeGroupSelect() {
        const {
            index,
            blockFilter,
            seasonStart,
            onRemove,
            maxGroupSize,
            minGroupSize,
            isCommercialUser,
            validationErrors,
            selection: {
                division_id: divisionId,
                preferred_range_start: preferredRangeStart,
                preferred_range_end: preferredRangeEnd,
                group_max_size: groupMaxSize, // this is used as selected group size
            },
        } = this.props;

        let dateRangeStart;
        let dateRangeEnd;
        if (!isEmpty(preferredRangeStart) && preferredRangeStart !== '') {
            dateRangeStart = gnoment(preferredRangeStart);
        }
        if (!isEmpty(preferredRangeEnd) && preferredRangeEnd !== '') {
            dateRangeEnd = gnoment(preferredRangeEnd);
        }
        const displayDate = dateRangeStart || gnoment(seasonStart);

        const divisionOptions = getSelectableSeasonalDivisions(
            this.props.divisions,
            isCommercialUser
        );

        return (
            <DateRangeGroupSizeItem
                index={index}
                currentDate={getCurrentTime(this.props)}
                blockFilter={blockFilter}
                handleChange={this.handleChange}
                selectedDateRangeStart={dateRangeStart}
                selectedDateRangeEnd={dateRangeEnd}
                selectedDivisionId={divisionId}
                selectedGroupSize={groupMaxSize}
                displayDate={displayDate}
                divisionChoices={divisionOptions}
                minGroupSize={minGroupSize}
                maxGroupSize={maxGroupSize}
                validationErrors={validationErrors}
                onRemove={() => onRemove(index)}
            />
        );
    }

    render() {
        const {
            enableLengthOfStaySelect,
            enableMinMaxGroupSelect,
            lotteryPreferenceType,
            index,
            divisions,
            enableMaxOnlyGroupSelect,
        } = this.props;

        if (lotteryPreferenceType === LOTTERY_PREFERENCE_TYPES.DATE_RANGE_GROUP_SIZE) {
            return this.renderRangeGroupSelect();
        }

        let disabledDate = false;
        if (Object.keys(this.props.divisions).length > 1) {
            disabledDate = this.props.selection.division_id === '';
        }
        const lowestGroupMax =
            this.props.selection.group_min_size === dateSelectionDefault.group_min_size
                ? this.props.minGroupSize
                : this.props.selection.group_min_size;
        const highestGroupMin =
            this.props.selection.group_max_size === dateSelectionDefault.group_max_size
                ? this.props.maxGroupSize
                : this.props.selection.group_max_size;
        const lengthOfStays = [];
        if (this.props.lengthOfStayLimit) {
            for (let i = 0; i < this.props.lengthOfStayLimit; i++) {
                lengthOfStays.push(i + 1);
            }
        }

        let selectedDate;
        if (
            this.props.selection.preferred_date &&
            !isEmpty(this.props.selection.preferred_date) &&
            this.props.selection.preferred_date !== ''
        ) {
            selectedDate = gnoment(this.props.selection.preferred_date);
        }
        const displayDate = selectedDate || gnoment(this.props.seasonStart);

        return (
            <div data-testid="preferred-dates-item" className="per-form-group-item">
                <div className="per-form-group-item-header">
                    <div className="per-form-group-item-title">Choice #{index + 1}</div>
                    <Button
                        appearance="subtle-danger"
                        id={`removeIcon-${index}`}
                        iconBeforeElement={<Icon iconName="delete" />}
                        onClick={() => this.props.onRemove(this.props.index)}
                        screenReaderTextBefore={`Remove choice ${this.props.index + 1} from the lottery preference list`}
                    />
                </div>
                <FlexRow alignItems="end">
                    {Object.keys(divisions).length > 1
                        ? this.renderDivisionSelect(index)
                        : null}
                    <FlexCol md={6}>
                        <div className="rec-form-item-wrap">
                            <div
                                className={get(
                                    this.props,
                                    'validationErrors.choiceRequired'
                                )}
                            >
                                <SingleDatePickerCalendar
                                    currentDate={getCurrentTime(this.props)}
                                    id={`dateSelect-${index}`}
                                    blockFilter={this.props.blockFilter}
                                    divisionId={this.props.selection.division_id}
                                    disabled={disabledDate}
                                    selectedDate={selectedDate}
                                    onChange={(value, id, isInvalid) => {
                                        return this.handleChange({
                                            preferred_date: value,
                                            isInvalid,
                                        });
                                    }}
                                    initialVisibleMonth={displayDate}
                                    renderLabels={false}
                                    blockNonLotteryDates
                                />
                            </div>
                        </div>
                    </FlexCol>
                    {enableLengthOfStaySelect
                        ? this.renderLengthOfStaySelect(lengthOfStays)
                        : null}
                    {enableMinMaxGroupSelect && !enableMaxOnlyGroupSelect
                        ? this.renderMinMaxGroupSelect(
                              lowestGroupMax,
                              highestGroupMin,
                              this.props.index
                          )
                        : null}
                    {enableMaxOnlyGroupSelect &&
                        this.renderMaxOnlyGroupSelect(
                            get(this.props, 'selection.group_max_size', 0),
                            this.props.maxGroupSize,
                            index
                        )}
                </FlexRow>
            </div>
        );
    }
}
